// @ts-check

/**
 * @file Renders the invoice detail route.
 */

import Text from "@kaspernj/api-maker/build/utils/text"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams, useRouter} from "expo-router"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Linking, Platform, View} from "react-native"
import Button from "../../../src/components/button"
import buildRoutePath from "../../../src/shared/build-route-path"
import Card from "../../../src/components/card"
import DetailValue from "../../../src/components/detail-value"
import errorMessage from "../../../src/shared/error-message"
import {Invoice} from "../../../src/frontend-models/invoice"
import Link from "../../../src/components/link"
import Modal from "../../../src/components/modal"
import {requiredRouteParam} from "../../../src/shared/route-params"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"
import ScreenTitle from "../../../src/components/screen-title"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import InvoiceLineModel from "../../../src/frontend-models/invoice-line" */
/** @import UploadedFileModel from "../../../src/frontend-models/uploaded-file" */
/** @import UserModel from "../../../src/frontend-models/user" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} InvoicesShowScreenProps */

/**
 * @typedef {object} InvoicesShowScreenState
 * @property {boolean} confirmingDeleteInvoice Whether the invoice delete confirmation is open.
 * @property {string} confirmingDeleteInvoiceLineId Invoice line id awaiting delete confirmation.
 * @property {Invoice | null} invoice Loaded invoice.
 * @property {boolean} loading Whether the invoice is loading.
 * @property {{kind: "error" | "success", text: string} | null} status Status message shown on the route.
 * @property {string | null} submittingAction Active action key while an operation is running.
 * @property {UploadedFileModel[]} uploadedFiles Uploaded files attached to the invoice.
 */

/**
 * Renders invoice details, relationships, lines, and management actions.
 * @augments {ShapeComponent<InvoicesShowScreenProps, InvoicesShowScreenState>}
 */
class InvoicesShowScreen extends ShapeComponent {
  /** @type {boolean} */
  pendingDeleteRedirect = false
  /** @type {string} */
  invoiceID
  /** @type {string} */
  locale = "en"
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  handlers = {}
  loadRequestId = 0
  /** @type {InvoicesShowScreenState} */
  state = {
    confirmingDeleteInvoice: false,
    confirmingDeleteInvoiceLineId: "",
    invoice: /** @type {Invoice | null} */ null,
    loading: true,
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null,
    submittingAction: /** @type {string | null} */ null,
    uploadedFiles:
      /** @type {UploadedFileModel[]} */ []
  }

  /**
   * Initializes translations, route params, and invoice loading.
   * @returns {void} Nothing.
   */
  setup() {
    const locale = useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const router = useRouter()

    const invoiceID = requiredRouteParam(params, "id", "Invoice ID")
    this.invoiceID = invoiceID
    this.locale = locale
    this.params = params
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    this.router = router
    useEffect(() => {
      // loadInvoice surfaces failures via `this.s.status`; swallow the
      // rejected promise here so a transient "not found" mid-navigation
      // (e.g. the row was just deleted) can't escape as an unhandled
      // rejection and crash Node's test runner.
      this.loadInvoice().catch(() => {})
    }, [invoiceID])
  }

  /**
   * Renders the invoice detail route.
   * @returns {ReactNode} Invoice detail content.
   */
  render() {
    const {invoice, loading, status} = this.s
    const screenTitle = invoice
      ? _("Invoice %{number}", {
          number: invoice.invoiceNo() || String(invoice.id())
        })
      : _("Invoices")
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="invoiceShowScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="invoiceShowScreen"
      >
        <ScreenTitle>{screenTitle}</ScreenTitle>
        <View
          testID="invoiceShowScreen/content"
        >
          {status &&
            <Text
              style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
                color: status.kind === "error" ? this.tt.colors.dangerText : this.tt.colors.successText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="invoiceShowScreen/status"
            >
              {status.text}
            </Text>
          }
          {invoice &&
            <>
              <Text
                style={styles[`screenTitle-${this.tt.themeName}`] ||= {
                  fontSize: 24,
                  fontWeight: 700,
                  marginBottom: 16
                }}
                testID="invoiceShowScreen/title"
              >
                {screenTitle}
              </Text>
              {this.renderManagePanel()}
              {this.renderDetailsAndAmounts()}
              {this.renderRelationshipsRow()}
              {this.renderInvoiceLines()}
            </>
          }
        </View>
        {this.renderDeleteInvoiceConfirm()}
        {this.renderDeleteInvoiceLineConfirm()}
      </View>
    )
  }

  /**
   * Renders invoice management actions.
   * @returns {ReactNode} Management panel or null when no invoice is loaded.
   */
  renderManagePanel() {
    const {invoice, submittingAction} = this.s
    if (!invoice) return null
    const state = invoice.state()
    const canFinish = state === "draft"
    const canRegisterAsSent = state === "draft" || state === "finished"
    const canRegisterAsPaid = state === "draft" || state === "sent"
    return (
      <Card
        style={styles.managePanelSpacing ||= {marginBottom: 16}}
        testID="invoiceShowScreen/managePanel"
      >
        <Text
          style={styles[`cardTitle-${this.tt.themeName}`] ||= {
            fontSize: 18,
            fontWeight: 700,
            marginBottom: 12
          }}
          testID="invoiceShowScreen/managePanel/title"
        >
          {_("Manage")}
        </Text>
        <View
          style={styles.manageActions ||= {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8
          }}
          testID="invoiceShowScreen/managePanel/actions"
        >
          <Button
            iconName="file-pdf"
            label={submittingAction === "pdf" ? _("Loading...") : _("PDF")}
            onPress={this.tt.onPdfPress}
            testID="invoiceShowScreen/pdfButton"
          />
          <Button
            iconName="clock"
            label={submittingAction === "addUninvoicedTimelogs" ? _("Loading...") : _("Add uninvoiced timelogs")}
            onPress={this.tt.onAddUninvoicedTimelogsPress}
            testID="invoiceShowScreen/addUninvoicedTimelogsButton"
            variant="secondary"
          />
          {canFinish &&
            <Button
              iconName="circle-check"
              label={submittingAction === "finish" ? _("Loading...") : _("Finish")}
              onPress={this.tt.onFinishPress}
              testID="invoiceShowScreen/finishButton"
              variant="secondary"
            />
          }
          {canRegisterAsSent &&
            <Button
              iconName="paper-plane"
              label={submittingAction === "registerAsSent" ? _("Loading...") : _("Register as sent")}
              onPress={this.tt.onRegisterAsSentPress}
              testID="invoiceShowScreen/registerAsSentButton"
              variant="secondary"
            />
          }
          {canRegisterAsPaid &&
            <Button
              iconName="money-bill"
              label={submittingAction === "registerAsPaid" ? _("Loading...") : _("Register as paid")}
              onPress={this.tt.onRegisterAsPaidPress}
              testID="invoiceShowScreen/registerAsPaidButton"
              variant="secondary"
            />
          }
        </View>
      </Card>
    )
  }

  /**
   * Renders invoice details and amount cards.
   * @returns {ReactNode} Detail cards or null when no invoice is loaded.
   */
  renderDetailsAndAmounts() {
    const {invoice, submittingAction} = this.s
    const {params} = this.tt
    if (!invoice) return null
    const organization = invoice.organization()
    const creditor = invoice.creditor()
    const user = invoice.user()
    return (
      <View
        style={styles.detailsRow ||= {
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 16
        }}
        testID="invoiceShowScreen/detailsRow"
      >
        <Card
          style={styles.detailsCardSpacing ||= {flex: 1, minWidth: 280}}
          testID="invoiceShowScreen/detailsCard"
        >
          <Text
            style={styles[`cardTitle-${this.tt.themeName}`]}
            testID="invoiceShowScreen/detailsCard/title"
          >
            {_("Details")}
          </Text>
          {detailRow({colors: this.tt.colors, label: _("ID"), testID: "id", themeName: this.tt.themeName, value: String(invoice.id())})}
          {detailRow({colors: this.tt.colors, label: _("Invoice no"), testID: "invoiceNo", themeName: this.tt.themeName, value: invoice.invoiceNo() || ""})}
          {detailRow({colors: this.tt.colors, label: _("State"), testID: "state", themeName: this.tt.themeName, value: invoice.translatedState() || invoice.state() || ""})}
          {detailRow({colors: this.tt.colors, label: _("Date"), testID: "date", themeName: this.tt.themeName, value: formatDate(invoice.date())})}
          {detailRow({colors: this.tt.colors, label: _("Organization"), testID: "organization", themeName: this.tt.themeName, value: organization?.name() || ""})}
          {detailRow({colors: this.tt.colors, label: _("Creditor"), testID: "creditor", themeName: this.tt.themeName, value: creditor?.name() || ""})}
          {detailRow({colors: this.tt.colors, label: _("User"), testID: "user", themeName: this.tt.themeName, value: displayUserName(user)})}
        </Card>
        <Card
          style={styles.amountsCardSpacing ||= {flex: 1, minWidth: 280}}
          testID="invoiceShowScreen/amountsCard"
        >
          <View
            style={styles.amountsHeader ||= {
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
              marginBottom: 12
            }}
            testID="invoiceShowScreen/amountsCard/header"
          >
            <Text
              style={styles[`cardTitle-${this.tt.themeName}`]}
              testID="invoiceShowScreen/amountsCard/title"
            >
              {_("Amounts")}
            </Text>
            <View
              style={styles.amountsActions ||= {
                flexDirection: "row",
                gap: 8
              }}
              testID="invoiceShowScreen/amountsCard/actions"
            >
              <Button
                href={buildRoutePath(params, `/invoices/${invoice.id()}/edit`)}
                iconName="pen-to-square"
                label={_("Edit")}
                testID="invoiceShowScreen/editLink"
                variant="outline"
              />
              <Button
                disabled={submittingAction === "deleteInvoice"}
                iconName="trash"
                label={submittingAction === "deleteInvoice" ? _("Loading...") : _("Delete")}
                onPress={this.tt.onDeleteInvoicePress}
                testID="invoiceShowScreen/deleteButton"
                variant="destructive-outline"
              />
            </View>
          </View>
          {detailRow({colors: this.tt.colors, label: _("Payment date"), testID: "paymentAt", themeName: this.tt.themeName, value: formatDate(invoice.paymentAt())})}
          {detailRow({colors: this.tt.colors, label: _("Invoice type"), testID: "invoiceType", themeName: this.tt.themeName, value: invoice.translatedInvoiceType() || invoice.invoiceType() || ""})}
          {detailRow({colors: this.tt.colors, label: _("No VAT"), testID: "noVat", themeName: this.tt.themeName, value: invoice.noVat() ? _("Yes") : _("No")})}
          {detailRow({colors: this.tt.colors, label: _("Amount"), testID: "amount", themeName: this.tt.themeName, value: formatMoneyFromCents(invoice.amountCents())})}
          {detailRow({colors: this.tt.colors, label: _("Amount VAT"), testID: "amountVat", themeName: this.tt.themeName, value: formatNumber(invoice.amountVat())})}
          {detailRow({colors: this.tt.colors, label: _("Amount total"), testID: "amountTotal", themeName: this.tt.themeName, value: formatNumber(invoice.amountTotal())})}
        </Card>
      </View>
    )
  }

  /**
   * Renders invoice group and uploaded file relationships.
   * @returns {ReactNode} Relationship cards or null when no invoice is loaded.
   */
  renderRelationshipsRow() {
    const {invoice, uploadedFiles} = this.s
    const {params} = this.tt
    if (!invoice) return null
    const invoiceGroups = invoice.invoiceGroupsLoaded()
    return (
      <View
        style={styles.relationshipsRow ||= {
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 16
        }}
        testID="invoiceShowScreen/relationshipsRow"
      >
        <Card
          style={styles.relationshipCardSpacing ||= {flex: 1, minWidth: 280}}
          testID="invoiceShowScreen/invoiceGroupsCard"
        >
          <Text
            style={styles[`cardTitle-${this.tt.themeName}`]}
            testID="invoiceShowScreen/invoiceGroupsCard/title"
          >
            {_("Invoice groups")}
          </Text>
          {invoiceGroups.length === 0 &&
            <Text
              style={styles[`emptyText-${this.tt.themeName}`] ||= {
                color: this.tt.colors.textMuted,
                fontSize: 14
              }}
              testID="invoiceShowScreen/invoiceGroupsCard/emptyText"
            >
              {_("No invoice groups")}
            </Text>
          }
          {invoiceGroups.map((invoiceGroup) => (
            <Link
              href={buildRoutePath(
                params,
                `/invoice-groups/${invoiceGroup.id()}`
              )}
              key={invoiceGroup.id()}
              style={styles[`relationshipLink-${this.tt.themeName}`] ||= {
                color: this.tt.colors.link,
                fontSize: 15,
                marginTop: 8
              }}
              testID={`invoiceShowScreen/invoiceGroup-${invoiceGroup.id()}/name`}
            >
              {invoiceGroup.name()}
            </Link>
          ))}
        </Card>
        <Card
          style={styles.relationshipCardSpacing}
          testID="invoiceShowScreen/uploadedFilesCard"
        >
          <Text
            style={styles[`cardTitle-${this.tt.themeName}`]}
            testID="invoiceShowScreen/uploadedFilesCard/title"
          >
            {_("Uploaded files")}
          </Text>
          {uploadedFiles.length === 0 &&
            <Text
              style={styles[`emptyText-${this.tt.themeName}`]}
              testID="invoiceShowScreen/uploadedFilesCard/emptyText"
            >
              {_("No uploaded files")}
            </Text>
          }
          {uploadedFiles.map((uploadedFile) => (
            <Link
              href={buildRoutePath(
                params,
                `/uploaded-files/${uploadedFile.id()}`
              )}
              key={uploadedFile.id()}
              style={styles[`relationshipLink-${this.tt.themeName}`]}
              testID={`invoiceShowScreen/uploadedFile-${uploadedFile.id()}/title`}
            >
              {uploadedFile.title() ||
                uploadedFile.fileName() ||
                _("Uploaded file")}
            </Link>
          ))}
        </Card>
      </View>
    )
  }

  /**
   * Renders invoice line rows.
   * @returns {ReactNode} Invoice line card or null when no invoice is loaded.
   */
  renderInvoiceLines() {
    const {invoice, submittingAction} = this.s
    const {params} = this.tt
    if (!invoice) return null
    const invoiceLines = invoice.invoiceLinesLoaded()
    return (
      <Card
        style={styles.invoiceLinesCardSpacing ||= {marginBottom: 16}}
        testID="invoiceShowScreen/invoiceLinesCard"
      >
        <View
          style={styles.invoiceLinesHeader ||= {
            alignItems: "center",
            flexDirection: "row",
            justifyContent: "space-between",
            marginBottom: 12
          }}
          testID="invoiceShowScreen/invoiceLinesCard/header"
        >
          <Text
            style={styles[`cardTitle-${this.tt.themeName}`]}
            testID="invoiceShowScreen/invoiceLinesCard/title"
          >
            {_("Invoice lines")}
          </Text>
          <Button
            href={buildRoutePath(
              params,
              `/invoice-lines/new?invoice_id=${invoice.id()}`
            )}
            iconName="plus"
            label={_("Add invoice line")}
            testID="invoiceShowScreen/addInvoiceLineLink"
            variant="outline"
          />
        </View>
        {invoiceLines.length === 0 &&
          <Text
            style={styles[`emptyText-${this.tt.themeName}`]}
            testID="invoiceShowScreen/invoiceLinesCard/emptyText"
          >
            {_("No invoice lines")}
          </Text>
        }
        {invoiceLines.map((invoiceLine) => {
          const invoiceLineID = String(invoiceLine.id())
          const timelog = invoiceLine.timelog()
          const themeName = this.tt.themeName
          const colors = this.tt.colors
          const lineLabelStyle = styles[`invoiceLineLabel-${themeName}`] ||= {
            color: colors.textMuted,
            fontSize: 12,
            fontWeight: 600
          }
          const lineValueStyle = styles[`invoiceLineValue-${themeName}`] ||= {fontSize: 14}
          return (
            <View
              key={invoiceLineID}
              style={styles[`invoiceLineRow-${themeName}`] ||= {
                borderTopColor: colors.divider,
                borderTopWidth: 1,
                paddingVertical: 10
              }}
              testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}`}
            >
              <View
                style={styles.invoiceLineColumns ||= {
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 12
                }}
                testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/columns`}
              >
                <View
                  style={styles.invoiceLineColumnWide ||= {flex: 2, minWidth: 180}}
                  testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/title`}
                >
                  <Text style={lineLabelStyle} testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/title/label`}>{_("Title")}</Text>
                  <Text style={lineValueStyle} testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/title/value`}>{invoiceLine.displayTitle() || invoiceLine.title() || "-"}</Text>
                </View>
                <View
                  style={styles.invoiceLineColumn ||= {flex: 1, minWidth: 100}}
                  testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/timelog`}
                >
                  <Text style={lineLabelStyle} testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/timelog/label`}>{_("Timelog")}</Text>
                  <Text style={lineValueStyle} testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/timelog/value`}>{timelog ? formatDate(timelog.date()) : "-"}</Text>
                </View>
                <View
                  style={styles.invoiceLineColumn}
                  testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/quantity`}
                >
                  <Text style={lineLabelStyle} testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/quantity/label`}>{_("Quantity")}</Text>
                  <Text style={lineValueStyle} testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/quantity/value`}>{formatQuantity(invoiceLine.quantity())}</Text>
                </View>
                <View
                  style={styles.invoiceLineColumn}
                  testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/amount`}
                >
                  <Text style={lineLabelStyle} testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/amount/label`}>{_("Amount")}</Text>
                  <Text style={lineValueStyle} testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/amount/value`}>{formatMoneyFromCents(invoiceLine.amountCents())}</Text>
                </View>
                <View
                  style={styles.invoiceLineColumn}
                  testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/total`}
                >
                  <Text style={lineLabelStyle} testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/total/label`}>{_("Total")}</Text>
                  <Text style={lineValueStyle} testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/total/value`}>{formatNumber(invoiceLineTotal(invoiceLine))}</Text>
                </View>
              </View>
              <View
                style={styles.invoiceLineActions ||= {
                  flexDirection: "row",
                  gap: 8,
                  justifyContent: "flex-end",
                  marginTop: 8
                }}
                testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/actions`}
              >
                <Button
                  href={buildRoutePath(params, `/invoice-lines/${invoiceLine.id()}/edit`)}
                  iconName="pen-to-square"
                  label={_("Edit")}
                  testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/editLink`}
                  variant="outline"
                />
                <Button
                  disabled={submittingAction === `deleteInvoiceLine-${invoiceLineID}`}
                  iconName="trash"
                  label={submittingAction === `deleteInvoiceLine-${invoiceLineID}` ? _("Loading...") : _("Delete")}
                  onPress={
                    this.handlers[`confirmDeleteInvoiceLine-${invoiceLineID}`] ||= () => {
                      this.s.confirmingDeleteInvoiceLineId = invoiceLineID
                      this.s.status = null
                    }
                  }
                  testID={`invoiceShowScreen/invoiceLine-${invoiceLineID}/deleteButton`}
                  variant="destructive-outline"
                />
              </View>
            </View>
          )
        })}
      </Card>
    )
  }

  /**
   * Renders the invoice delete confirmation modal.
   * @returns {ReactNode} Delete confirmation modal or null.
   */
  renderDeleteInvoiceConfirm() {
    const {confirmingDeleteInvoice, submittingAction} = this.s
    return (
      <Modal
        cardStyle={styles.confirmCardSpacing ||= {
          borderRadius: 12,
          maxWidth: 360,
          padding: 20
        }}
        isVisible={confirmingDeleteInvoice}
        onDismiss={this.tt.onDeleteInvoiceConfirmDismiss}
        onRequestClose={this.tt.onDeleteInvoiceCancelPress}
        testID="invoiceShowScreen/deleteConfirm"
      >
          <Text
            style={styles[`confirmTitle-${this.tt.themeName}`] ||= {
              fontSize: 18,
              fontWeight: 700,
              marginBottom: 8
            }}
            testID="invoiceShowScreen/deleteConfirm/title"
          >
            {_("Delete invoice")}
          </Text>
          <Text
            style={styles[`confirmBody-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              fontSize: 14,
              marginBottom: 16
            }}
            testID="invoiceShowScreen/deleteConfirm/body"
          >
            {_("Delete this invoice?")}
          </Text>
          <View
            style={styles.confirmActions ||= {
              flexDirection: "row",
              gap: 8,
              justifyContent: "flex-end"
            }}
            testID="invoiceShowScreen/deleteConfirm/actions"
          >
            <Button
              iconName="xmark"
              label={_("Cancel")}
              onPress={this.tt.onDeleteInvoiceCancelPress}
              testID="invoiceShowScreen/deleteConfirm/cancelButton"
              variant="outline"
            />
            <Button
              disabled={submittingAction === "deleteInvoice"}
              iconName="trash"
              label={submittingAction === "deleteInvoice" ? _("Loading...") : _("Delete")}
              onPress={this.tt.onDeleteInvoiceConfirmPress}
              testID="invoiceShowScreen/deleteConfirm/confirmButton"
              variant="destructive"
            />
          </View>
      </Modal>
    )
  }

  /**
   * Renders the invoice line delete confirmation modal.
   * @returns {ReactNode} Delete confirmation modal or null.
   */
  renderDeleteInvoiceLineConfirm() {
    const {confirmingDeleteInvoiceLineId, submittingAction} = this.s
    return (
      <Modal
        cardStyle={styles.confirmCardSpacing}
        isVisible={Boolean(confirmingDeleteInvoiceLineId)}
        onRequestClose={this.tt.onDeleteInvoiceLineCancelPress}
        testID="invoiceShowScreen/deleteInvoiceLineConfirm"
      >
          <Text
            style={styles[`confirmTitle-${this.tt.themeName}`]}
            testID="invoiceShowScreen/deleteInvoiceLineConfirm/title"
          >
            {_("Delete invoice line")}
          </Text>
          <Text
            style={styles[`confirmBody-${this.tt.themeName}`]}
            testID="invoiceShowScreen/deleteInvoiceLineConfirm/body"
          >
            {_("Delete this invoice line?")}
          </Text>
          <View
            style={styles.confirmActions}
            testID="invoiceShowScreen/deleteInvoiceLineConfirm/actions"
          >
            <Button
              iconName="xmark"
              label={_("Cancel")}
              onPress={this.tt.onDeleteInvoiceLineCancelPress}
              testID="invoiceShowScreen/deleteInvoiceLineConfirm/cancelButton"
              variant="outline"
            />
            <Button
              disabled={submittingAction === `deleteInvoiceLine-${confirmingDeleteInvoiceLineId}`}
              iconName="trash"
              label={
                submittingAction === `deleteInvoiceLine-${confirmingDeleteInvoiceLineId}`
                  ? _("Loading...")
                  : _("Delete")
              }
              onPress={this.tt.onDeleteInvoiceLineConfirmPress}
              testID="invoiceShowScreen/deleteInvoiceLineConfirm/confirmButton"
              variant="destructive"
            />
          </View>
      </Modal>
    )
  }

  /**
   * Adds all uninvoiced timelogs to the invoice.
   * @returns {Promise<void>} Resolves after the invoice is reloaded.
   * @rejects {Error} When adding timelogs fails.
   */
  onAddUninvoicedTimelogsPress = async () => {
    const {invoice} = this.s
    if (!invoice) return
    this.s.status = null
    this.s.submittingAction = "addUninvoicedTimelogs"
    try {
      const response = await invoice.addUninvoicedTimelogs()
      await this.reloadInvoice()
      this.s.status = {
        kind: "success",
        text: _("Added %{count} uninvoiced timelogs", {
          count: String(response.createdCount || 0)
        })
      }
      this.s.submittingAction = null
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      this.s.submittingAction = null
      throw error
    }
  }

  /**
   * Opens invoice delete confirmation.
   * @returns {void} Nothing.
   */
  onDeleteInvoicePress = () => {
    this.s.confirmingDeleteInvoice = true
    this.s.status = null
  }

  /**
   * Closes invoice delete confirmation.
   * @returns {void} Nothing.
   */
  onDeleteInvoiceCancelPress = () => {
    this.s.confirmingDeleteInvoice = false
  }

  /**
   * Deletes the invoice and its dependent records.
   * @returns {Promise<void>} Resolves after navigation to the invoice list.
   * @rejects {Error} When deletion fails.
   */
  onDeleteInvoiceConfirmPress = async () => {
    const {invoice, uploadedFiles} = this.s
    if (!invoice) return
    this.s.submittingAction = "deleteInvoice"
    try {
      for (const invoiceLine of invoice.invoiceLinesLoaded()) {
        await invoiceLine.destroy()
      }
      for (const invoiceGroupLink of invoice.invoiceGroupLinksLoaded()) {
        await invoiceGroupLink.destroy()
      }
      for (const uploadedFile of uploadedFiles) {
        await uploadedFile.destroy()
      }
      await invoice.destroy()
      this.pendingDeleteRedirect = true
      this.s.confirmingDeleteInvoice = false
    } catch (error) {
      this.s.confirmingDeleteInvoice = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      this.s.submittingAction = null
      throw error
    }
  }

  /**
   * Navigates to the invoice list after the delete-confirm modal finishes
   * fading out. Replacing the route immediately would remount the screen and
   * drop the modal before it could animate.
   * @returns {void} Nothing.
   */
  onDeleteInvoiceConfirmDismiss = () => {
    if (!this.pendingDeleteRedirect) return
    this.pendingDeleteRedirect = false
    this.tt.router.replace(buildRoutePath(this.tt.params, "/invoices"))
  }

  /**
   * Closes invoice line delete confirmation.
   * @returns {void} Nothing.
   */
  onDeleteInvoiceLineCancelPress = () => {
    this.s.confirmingDeleteInvoiceLineId = ""
  }

  /**
   * Deletes the selected invoice line.
   * @returns {Promise<void>} Resolves after the invoice is reloaded.
   * @rejects {Error} When invoice line deletion fails.
   */
  onDeleteInvoiceLineConfirmPress = async () => {
    const {invoice, confirmingDeleteInvoiceLineId} = this.s
    if (!invoice || !confirmingDeleteInvoiceLineId) return
    const invoiceLine = invoice
      .invoiceLinesLoaded()
      .find((candidate) => String(candidate.id()) === confirmingDeleteInvoiceLineId)
    if (!invoiceLine) {
      this.s.confirmingDeleteInvoiceLineId = ""
      this.s.status = {
        kind: "error",
        text: _("Invoice line not found")
      }
      return
    }
    this.s.submittingAction = `deleteInvoiceLine-${confirmingDeleteInvoiceLineId}`
    try {
      await invoiceLine.destroy()
      await this.reloadInvoice()
      this.s.confirmingDeleteInvoiceLineId = ""
      this.s.status = {
        kind: "success",
        text: _("The invoice line was deleted")
      }
      this.s.submittingAction = null
    } catch (error) {
      this.s.confirmingDeleteInvoiceLineId = ""
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      this.s.submittingAction = null
      throw error
    }
  }

  /**
   * Finishes the invoice.
   * @returns {Promise<void>} Resolves after the invoice state is updated.
   */
  onFinishPress = async () => {
    await this.runStateCommand(
      "finish",
      async (invoice) => await invoice.finish(),
      "Invoice finished"
    )
  }

  /**
   * Opens the invoice PDF.
   * @returns {Promise<void>} Resolves after the PDF payload is opened.
   * @rejects {Error} When PDF generation or opening fails.
   */
  onPdfPress = async () => {
    const {invoice} = this.s
    if (!invoice) return
    this.s.status = null
    this.s.submittingAction = "pdf"
    try {
      const payload = await invoice.pdf({locale: this.locale})
      await openPdfPayload(payload)
      this.s.submittingAction = null
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      this.s.submittingAction = null
      throw error
    }
  }

  /**
   * Registers the invoice as paid.
   * @returns {Promise<void>} Resolves after the invoice state is updated.
   */
  onRegisterAsPaidPress = async () => {
    await this.runStateCommand(
      "registerAsPaid",
      async (invoice) => await invoice.registerAsPaid(),
      "Invoice registered as paid"
    )
  }

  /**
   * Registers the invoice as sent.
   * @returns {Promise<void>} Resolves after the invoice state is updated.
   */
  onRegisterAsSentPress = async () => {
    await this.runStateCommand(
      "registerAsSent",
      async (invoice) => await invoice.registerAsSent(),
      "Invoice registered as sent"
    )
  }

  /**
   * Runs an invoice state command and reloads the invoice.
   * @param {"finish" | "registerAsPaid" | "registerAsSent"} actionName Action key used for loading state.
   * @param {(invoice: Invoice) => Promise<unknown>} callback Command callback to run for the invoice.
   * @param {string} successMessage Message to show after a successful command.
   * @returns {Promise<void>} Resolves after the command and reload finish.
   * @rejects {Error} When the command fails.
   */
  async runStateCommand(actionName, callback, successMessage) {
    const {invoice} = this.s
    if (!invoice) return
    this.s.status = null
    this.s.submittingAction = actionName
    try {
      await callback(invoice)
      await this.reloadInvoice()
      this.s.status = {
        kind: "success",
        text: _(successMessage)
      }
      this.s.submittingAction = null
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      this.s.submittingAction = null
      throw error
    }
  }

  /**
   * Loads the invoice for the current route.
   * @returns {Promise<void>} Resolves after invoice state is updated.
   * @rejects {Error} When invoice loading fails.
   */
  async loadInvoice() {
    const requestId = this.loadRequestId + 1
    this.loadRequestId = requestId
    this.s.loading = true
    this.s.status = null
    try {
      const loadedInvoice = await this.findInvoice()
      if (this.loadRequestId !== requestId) return
      if (!loadedInvoice) {
        this.s.invoice = null
        this.s.loading = false
        this.s.status = {
          kind: "error",
          text: _("Invoice not found")
        }
        this.s.uploadedFiles = []
        return
      }
      this.s.invoice = loadedInvoice
      this.s.loading = false
      this.s.uploadedFiles = uploadedFilesForInvoice(loadedInvoice)
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      this.s.invoice = null
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      this.s.uploadedFiles = []
      throw error
    }
  }

  /**
   * Finds the routed invoice.
   * @returns {Promise<Invoice | null>} Loaded invoice or null when the route has no id.
   */
  async findInvoice() {
    const {invoiceID} = this.tt
    if (!invoiceID) return null
    const invoice = await Invoice
      .where({id: invoiceID})
      .preload({
        creditor: true,
        invoiceGroupLinks: {invoiceGroup: true},
        invoiceLines: {timelog: true},
        organization: true,
        uploadedFiles: true,
        user: true
      })
      .first()
    if (invoice) {
      invoice
        .invoiceGroups()
        .setLoaded(
          invoice
            .invoiceGroupLinksLoaded()
            .map((invoiceGroupLink) => invoiceGroupLink.invoiceGroup())
            .filter(Boolean)
        )
    }
    return invoice
  }

  /**
   * Reloads the current invoice and its relationships.
   * @returns {Promise<void>} Resolves after the invoice state is refreshed.
   */
  async reloadInvoice() {
    const invoice = await this.findInvoice()
    if (!invoice) {
      this.s.invoice = null
      this.s.uploadedFiles = []
      return
    }
    this.s.invoice = invoice
    this.s.uploadedFiles = uploadedFilesForInvoice(invoice)
  }
}

/** @type {NamedExoticComponent<InvoicesShowScreenProps>} */
const InvoicesShowScreenComponent = memo(shapeComponent(InvoicesShowScreen))

export default InvoicesShowScreenComponent

/**
 * Returns preloaded invoice uploads in the same order as the previous direct query.
 * @param {Invoice} invoice Invoice whose uploaded files are preloaded.
 * @returns {UploadedFileModel[]} Uploaded files sorted by id.
 */
function uploadedFilesForInvoice(invoice) {
  return invoice
    .uploadedFilesLoaded()
    .slice()
    .sort((left, right) => left.id().localeCompare(right.id()))
}

/**
 * Renders a labeled invoice detail row.
 * @param {{colors: import("../../../src/shared/theme").BoardThemeColors, label: string, testID: string, themeName: string, value: string}} args Detail row values.
 * @returns {ReactNode} Detail row content.
 */
function detailRow({colors, label, testID, themeName, value}) {
  const displayValue = value || "-"

  return (
    <View
      collapsable={false}
      style={styles[`detailRow-${themeName}`] ||= {
        borderBottomColor: colors.divider,
        borderBottomWidth: 1,
        paddingVertical: 8
      }}
      testID={`invoiceShowScreen/${testID}/row`}
    >
      <Text
        style={styles[`detailLabel-${themeName}`] ||= {
          color: colors.textMuted,
          fontWeight: 600,
          marginBottom: 2
        }}
        testID={`invoiceShowScreen/${testID}/label`}
      >
        {label}
      </Text>
      <DetailValue
        style={styles[`detailValue-${themeName}`] ||= {fontSize: 15}}
        testID={`invoiceShowScreen/${testID}/value`}
        value={displayValue}
      />
    </View>
  )
}

/**
 * Formats cents as a localized amount.
 * @param {number | null | undefined} value Amount in cents.
 * @returns {string} Localized amount label.
 */
function formatMoneyFromCents(value) {
  if (value === null || value === undefined) return "-"
  return formatNumber(value / 100)
}

/**
 * Formats a number with two decimal places.
 * @param {number | null | undefined} value Numeric value to format.
 * @returns {string} Localized number label.
 */
function formatNumber(value) {
  if (value === null || value === undefined) return "-"
  return Number(value).toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  })
}

/**
 * Formats a date-like value for display.
 * @param {Date | string | null | undefined} value Date value to format.
 * @returns {string} Localized date label.
 */
function formatDate(value) {
  if (!value) return "-"
  const dateValue = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(dateValue.getTime())) return "-"
  return dateValue.toLocaleDateString()
}

/**
 * Formats an invoice line quantity.
 * @param {number | null | undefined} value Quantity value to format.
 * @returns {string} Localized quantity label.
 */
function formatQuantity(value) {
  if (value === null || value === undefined) return "-"
  return Number(value).toLocaleString(undefined, {
    maximumFractionDigits: 2
  })
}

/**
 * Calculates an invoice line total.
 * @param {InvoiceLineModel} invoiceLine Invoice line to calculate.
 * @returns {number | null} Total amount or null when required values are missing.
 */
function invoiceLineTotal(invoiceLine) {
  const amountCents = invoiceLine.amountCents()
  const quantity = invoiceLine.quantity()
  if (amountCents === null || quantity === null) return null
  return (amountCents / 100) * quantity
}

/**
 * Builds a user display name.
 * @param {UserModel | null} user User to display.
 * @returns {string} Combined name or email label.
 */
function displayUserName(user) {
  if (!user) return ""
  return (
    `${user.firstName() || ""} ${user.lastName() || ""}`.trim() ||
    user.email() ||
    ""
  )
}

/**
 * Opens an invoice PDF payload.
 * @param {{contentBase64?: string, contentType?: string, filename?: string}} payload PDF payload from the backend.
 * @returns {Promise<void>} Resolves after the payload is opened.
 * @rejects {Error} When the payload is invalid or cannot be opened.
 */
async function openPdfPayload(payload) {
  if (!payload.contentBase64) {
    throw new Error("PDF content was missing")
  }
  if (Platform.OS === "web" && typeof window !== "undefined") {
    const binary = window.atob(payload.contentBase64)
    const bytes = Uint8Array.from(binary, (character) =>
      character.charCodeAt(0)
    )
    const blob = new window.Blob([bytes], {
      type: payload.contentType || "application/pdf"
    })
    const objectUrl = window.URL.createObjectURL(blob)
    window.open(objectUrl, "_blank", "noopener")
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000)
    return
  }
  await Linking.openURL(
    `data:${payload.contentType || "application/pdf"};base64,${payload.contentBase64}`
  )
}
